import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import fr from './locales/fr.json';

export const SUPPORTED_LANGUAGES = ['en', 'fr'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      fr: { translation: fr },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

/**
 * Keep `<html lang>` in step with the active language (WCAG 2.1 — 3.1.1
 * Language of Page, Level A).
 *
 * index.html hardcodes `lang="en-CA"`. Nothing updated it, so every French
 * session served French content while telling assistive tech it was English —
 * screen readers then pronounce it with English phonetics, which is close to
 * unintelligible. A guaranteed Level A failure on a deliberately bilingual
 * product, and invisible to sighted testing.
 *
 * Region-qualified because the product is Canadian and the copy is written for
 * Canadian French / Canadian English.
 */
const HTML_LANG: Record<string, string> = { en: 'en-CA', fr: 'fr-CA' };

function syncDocumentLanguage(lng: string): void {
  if (typeof document === 'undefined') return;
  const base = lng.split('-')[0] ?? 'en';
  document.documentElement.lang = HTML_LANG[base] ?? base;
}

syncDocumentLanguage(i18n.language);
i18n.on('languageChanged', syncDocumentLanguage);

export default i18n;
